import React, { useState, useMemo, useRef, useEffect } from 'react';

/**
 * Pick a vulnerability template and fill the finding form from it.
 *
 * A search box rather than a dropdown: the library is now hundreds of
 * templates, and a native select of that length is a scroll bar and a guess.
 * Matching is on title, category and severity, so "xss", "injection" and
 * "critical" all narrow it.
 *
 * It fills the form rather than creating the finding outright, which is the
 * point of moving it here: the tester edits the observation and endpoints for
 * the engagement in front of them before anything is saved. The previous
 * control created a finished finding from the list page with no chance to
 * adjust it first.
 */

const SEVERITY_COLOR = {
  Critical: '#7f1d1d', High: '#dc2626', Medium: '#ea580c',
  Low: '#ca8a04', Info: '#2e86c1',
};

const MAX_SHOWN = 60;

export default function TemplatePicker({ templates, onPick, disabled }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [applied, setApplied] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open]);

  const matches = useMemo(() => {
    const list = Array.isArray(templates) ? templates : [];
    const needle = q.trim().toLowerCase();
    const scored = list.filter(t => {
      if (!needle) return true;
      return [t.title, t.category, t.severity, t.owasp_category, t.platforms]
        .some(v => String(v || '').toLowerCase().includes(needle));
    });
    // Title first, so a search for "xss" puts the XSS templates above the ones
    // that merely mention it in a category.
    scored.sort((a, b) => {
      if (needle) {
        const at = String(a.title || '').toLowerCase().includes(needle) ? 0 : 1;
        const bt = String(b.title || '').toLowerCase().includes(needle) ? 0 : 1;
        if (at !== bt) return at - bt;
      }
      return String(a.title || '').localeCompare(String(b.title || ''));
    });
    return scored;
  }, [templates, q]);

  const pick = (t) => {
    onPick(t);
    setApplied(t.title);
    setQ('');
    setOpen(false);
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: 13, boxSizing: 'border-box',
    border: '1px solid var(--border)', borderRadius: 'var(--radius)',
    background: 'var(--bg-input)', color: 'var(--text)', fontFamily: 'inherit',
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500,
                      color: 'var(--text-secondary)', marginBottom: 5 }}>
        Import from Template
      </label>
      <input
        value={q}
        disabled={disabled}
        placeholder={`Search ${(templates || []).length} templates by title, category or severity…`}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={inputStyle}
      />
      {applied && !open && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Filled from <strong style={{ color: 'var(--text-secondary)' }}>{applied}</strong>. Edit anything below before saving.
        </div>
      )}

      {open && (
        <div style={{
          position: 'absolute', zIndex: 50, left: 0, right: 0, top: '100%', marginTop: 4,
          maxHeight: 280, overflowY: 'auto', background: 'var(--bg-card)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-lg)',
        }}>
          {matches.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--text-muted)' }}>
              Nothing matches “{q}”.
            </div>
          )}
          {matches.slice(0, MAX_SHOWN).map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => pick(t)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                padding: '7px 10px', background: 'none', border: 'none',
                borderBottom: '1px solid var(--border)', fontFamily: 'inherit',
              }}
              onMouseOver={e => { e.currentTarget.style.background = 'var(--bg-input)'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{
                  flexShrink: 0, fontSize: 9, fontWeight: 700, color: '#fff',
                  padding: '1px 5px', borderRadius: 3,
                  background: SEVERITY_COLOR[t.severity] || '#6b7280',
                }}>{t.severity}</span>
                <span style={{ fontSize: 12, color: 'var(--text)', overflow: 'hidden',
                               textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                {[t.category, t.owasp_category, t.platforms].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}
          {matches.length > MAX_SHOWN && (
            <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--text-muted)' }}>
              {matches.length - MAX_SHOWN} more — keep typing to narrow it.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
